import React from 'react'
import PropTypes from 'prop-types'
import { DataGridPro } from '@mui/x-data-grid-pro'

import './CDataGrid.css'
import CButton from './CButton'

const CDataGrid = ({
    rows = [],
    columns = [],
    rowCount = 0,
    loading = false,
    autoHeight = false,
    checkboxSelection = false,
    disableRowSelectionOnClick = true,
    pageSizeOptions = [5, 10, 25, 50, 100],
    initialPageSize = 10,
    paginationModel = null,
    onPaginationModelChange = null,
    density = 'standard',
    className = '',
    onRowClick = null,
    onRowSelectionModelChange = null,
    getRowId = null,
    rowHeight = 52,
    headerHeight = 56,
    sortingMode = 'client',
    paginationMode = 'server',
    filterMode = 'client',
    disableColumnMenu = false,
    disableColumnFilter = false,
    disableColumnSelector = false,
    disableDensitySelector = false,
    hideFooter = false,
    hideFooterPagination = false,
    emptyStateImage = null,
    emptyStateTitle = 'No data available',
    emptyStateDescription = 'There are no records to display.',
    emptyStateButtonLabel = 'Add New',
    emptyStateOnClick = null,
    sx = {},
    ...props
}) => {
    // Handle row selection and convert IDs to full row data
    const onRowsSelectionHandler = (selectionModel) => {
        let selectedRowsData = []

        // Handle DataGridPro's selection model structure
        if (selectionModel && typeof selectionModel === 'object' && 'type' in selectionModel) {
            // DataGridPro format: { type: "exclude" | "include", ids: Set }
            const idsSet = selectionModel.ids instanceof Set ? selectionModel.ids : new Set(Object.keys(selectionModel.ids || {}))

            if (selectionModel.type === 'exclude') {
                // "exclude" means all rows except these IDs are selected
                selectedRowsData = rows.filter((row) => {
                    const rowId = getRowId ? getRowId(row) : row.id
                    return !idsSet.has(rowId)
                })
            } else {
                // "include" means only these IDs are selected
                selectedRowsData = rows.filter((row) => {
                    const rowId = getRowId ? getRowId(row) : row.id
                    return idsSet.has(rowId)
                })
            }
        } else {
            // Handle simple array format [id1, id2, ...]
            let ids = []
            if (Array.isArray(selectionModel)) {
                ids = selectionModel
            } else if (selectionModel instanceof Set) {
                ids = Array.from(selectionModel)
            }

            selectedRowsData = ids
                .map((id) => {
                    if (getRowId) {
                        return rows.find((row) => getRowId(row) === id)
                    }
                    return rows.find((row) => row.id === id)
                })
                .filter(Boolean)
        }

        if (onRowSelectionModelChange && typeof onRowSelectionModelChange === 'function') {
            onRowSelectionModelChange(selectedRowsData)
        }
    }

    // When no data is returned from the API
    function CustomNoRowsOverlay() {
        return (
            <div className="empty-state">
                {emptyStateImage && <img src={emptyStateImage} alt="No users found" />}
                <div className="empty-state-text-container">
                    <span className="empty-state-text header-4">{emptyStateTitle}</span>
                    <span className="empty-state-text text-4">{emptyStateDescription}</span>
                    <CButton
                        label={emptyStateButtonLabel}
                        onClick={emptyStateOnClick}
                        variant="outlined"
                        color="secondary"
                        leftIcon={"add-circle"}
                    />
                </div>
            </div>
        );
    }

    const dataGridProps = {
        rows,
        columns,
        rowCount,
        loading,
        autoHeight,
        checkboxSelection,
        disableRowSelectionOnClick,
        pagination: true,
        pageSizeOptions,
        ...(paginationModel && { paginationModel }),
        ...(onPaginationModelChange && { onPaginationModelChange }),
        ...(!paginationModel && {
            initialState: {
                pagination: {
                    paginationModel: { pageSize: initialPageSize },
                },
            },
        }),
        density,
        rowHeight,
        headerHeight,
        sortingMode,
        paginationMode,
        filterMode,
        disableColumnMenu,
        disableColumnFilter,
        disableColumnSelector,
        disableDensitySelector,
        hideFooter,
        hideFooterPagination,
        slots: {
            noRowsOverlay: CustomNoRowsOverlay,

        },
        ...(sx && { sx }),
        ...(onRowClick && { onRowClick }),
        ...(onRowSelectionModelChange && { onRowSelectionModelChange: onRowsSelectionHandler }),
        ...(getRowId && { getRowId }),
        ...props,
    }

    return (
        <div className={`c-data-grid ${className}`}>
            <DataGridPro {...dataGridProps} />
        </div>
    )
}

CDataGrid.propTypes = {
    rows: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    autoHeight: PropTypes.bool,
    paginationModel: PropTypes.object,
    onPaginationModelChange: PropTypes.func,
    checkboxSelection: PropTypes.bool,
    disableRowSelectionOnClick: PropTypes.bool,
    pageSizeOptions: PropTypes.array,
    initialPageSize: PropTypes.number,
    density: PropTypes.oneOf(['compact', 'standard', 'comfortable']),
    className: PropTypes.string,
    onRowClick: PropTypes.func,
    onRowSelectionModelChange: PropTypes.func,
    getRowId: PropTypes.func,
    rowHeight: PropTypes.number,
    headerHeight: PropTypes.number,
    sortingMode: PropTypes.oneOf(['client', 'server']),
    paginationMode: PropTypes.oneOf(['client', 'server']),
    filterMode: PropTypes.oneOf(['client', 'server']),
    disableColumnMenu: PropTypes.bool,
    disableColumnFilter: PropTypes.bool,
    disableColumnSelector: PropTypes.bool,
    disableDensitySelector: PropTypes.bool,
    hideFooter: PropTypes.bool,
    hideFooterPagination: PropTypes.bool,
    emptyStateImage: PropTypes.string,
    emptyStateTitle: PropTypes.string,
    emptyStateDescription: PropTypes.string,
    emptyStateButtonLabel: PropTypes.string,
    emptyStateOnClick: PropTypes.func,
    sx: PropTypes.object,
}

export default CDataGrid
